import { Edit2, ImageOff, Minus, Plus, Ticket, Trash2 } from "lucide-react";

import { itemEditavel, resumoEtapas } from "./resumoEtapas";
import { formatarQtd, medidaDoItem } from "../../lib/unidadeVenda";

const reais = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

// Dois tamanhos do mesmo cartão. `normal` é o popup do celular (não mudar sem
// conferir no celular); `grande` é a sacola lateral do computador — foto e
// botões maiores, crescendo de novo a partir de 1280 px (pedido do dono,
// 02/10/2026: "os botões da sacola estão muito pequenos, deve ser responsivo").
const TAMANHOS = {
  normal: {
    cartao: "p-2", foto: "w-12 h-12", semFoto: "w-5 h-5", nome: "text-xs", preco: "text-xs",
    linha: "gap-1.5 mt-1", botao: "w-5 h-5 rounded", icone: "w-2.5 h-2.5", qtd: "text-xs", qtdUnidade: "w-4",
    etapaFoto: "w-7 h-7", etapaNome: "text-[11px]", etapaPreco: "text-[10px]",
  },
  grande: {
    cartao: "p-2.5 xl:p-3", foto: "w-14 h-14 xl:w-16 xl:h-16", semFoto: "w-6 h-6", nome: "text-sm", preco: "text-sm",
    linha: "gap-2 mt-1.5", botao: "w-7 h-7 xl:w-8 xl:h-8 rounded-md", icone: "w-3.5 h-3.5 xl:w-4 xl:h-4", qtd: "text-sm", qtdUnidade: "min-w-[1.5rem]",
    etapaFoto: "w-8 h-8", etapaNome: "text-xs", etapaPreco: "text-[11px]",
  },
};

// Um item da sacola: foto, nome, preço, − / + / lixeira, lápis de editar,
// escolhas por etapa e observação. Mesmo cartão no popup do celular e na
// lateral do computador.
//
// `semCupom` vem pronto da página (a regra mora em features/ofertas-do-dia,
// que esta feature não importa); `passoPeso` é o mínimo de quilo/litro.
export default function ItemSacola({
  item, produtosFoto = [], produtosVenda = [], t, darkMode, fotoDe, semCupom = false, passoPeso = 0.1,
  grande = false, onRemover, onAlterarQtd, onEditar,
}) {
  const tam = grande ? TAMANHOS.grande : TAMANHOS.normal;
  return (
    <div className={`${t.bgCartItem} rounded-lg ${tam.cartao}`} data-e2e="item-sacola">
      <div className={`flex ${grande ? "gap-3" : "gap-2"}`}>
        <FotoItem url={item.photo_url} t={t} darkMode={darkMode} fotoDe={fotoDe} tam={tam} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h4 className={`font-medium ${t.text} ${tam.nome} truncate`}>{item.name}</h4>
            {item.combo_type && (
              <span className={`text-[9px] px-1 py-0.5 rounded ${item.combo_type === "combo" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"}`}>
                {item.combo_type === "combo" ? "COMBO" : "SIMPLES"}
              </span>
            )}
          </div>
          <p className={`text-orange-500 font-semibold ${tam.preco}`}>
            {reais((item.final_price || item.sale_price) * item.quantity)}
          </p>
          {semCupom && (
            <p className={`text-[10px] ${t.textMuted} flex items-center gap-1 mt-0.5`} data-e2e="item-sem-cupom">
              <Ticket className="w-3 h-3" /> Não vale com cupom de desconto
            </p>
          )}
          {/* v1.5.358+: "Peça de novo" recalculou o preço da pizza (pricing_rule mudou) */}
          {item._price_updated && (
            <p className="text-amber-500 text-[10px] font-medium mt-0.5 flex items-center gap-1">
              ⚠️ Preço atualizado: era {reais(item._price_updated.oldPrice)}
            </p>
          )}
          <ControlesQtd item={item} produtosVenda={produtosVenda} t={t} passoPeso={passoPeso} tam={tam}
            onRemover={onRemover} onAlterarQtd={onAlterarQtd} onEditar={onEditar} />
        </div>
      </div>
      <EtapasDoItem etapas={resumoEtapas(item, produtosFoto)} t={t} fotoDe={fotoDe} tam={tam} />
      {item.observation && (
        <div className={`mt-2 pt-2 border-t ${t.border}`}>
          <p className={`text-[9px] ${t.textMuted} italic`}>📝 {item.observation}</p>
        </div>
      )}
    </div>
  );
}

function FotoItem({ url, t, darkMode, fotoDe, tam }) {
  return (
    <div className={`${tam.foto} ${t.bgMuted} rounded-lg flex-shrink-0 overflow-hidden`}>
      {url ? (
        <img src={fotoDe(url)} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className={`w-full h-full flex items-center justify-center ${darkMode ? "text-zinc-500" : "text-zinc-400"}`}><ImageOff className={tam.semFoto} strokeWidth={1.5} /></div>
      )}
    </div>
  );
}

function ControlesQtd({ item, produtosVenda, t, passoPeso, tam, onRemover, onAlterarQtd, onEditar }) {
  // Quilo/litro: "0,350 kg" no lugar de "0.35", e a lixeira só no mínimo (0,1)
  const medida = medidaDoItem(item, produtosVenda);
  const noMinimo = Number(item.quantity) <= (medida ? passoPeso + 1e-9 : 1);
  const botao = `${tam.botao} ${t.bgMuted} flex items-center justify-center`;
  return (
    <div className={`flex items-center ${tam.linha}`}>
      {/* Botão menos vira lixeira no mínimo (1 un ou 0,1 kg/L) */}
      {noMinimo ? (
        <button onClick={onRemover} className={`${tam.botao} bg-red-500/20 flex items-center justify-center`} title="Remover do carrinho" aria-label="Remover do carrinho">
          <Trash2 className={`${tam.icone} text-red-400`} />
        </button>
      ) : (
        <button onClick={() => onAlterarQtd(-1)} className={botao} aria-label="Diminuir quantidade">
          <Minus className={tam.icone} />
        </button>
      )}
      <span className={`${t.text} font-medium ${tam.qtd} text-center ${medida ? "px-1 whitespace-nowrap" : tam.qtdUnidade}`}>{medida ? formatarQtd(item.quantity, medida) : item.quantity}</span>
      <button onClick={() => onAlterarQtd(1)} className={botao} aria-label="Aumentar quantidade">
        <Plus className={tam.icone} />
      </button>
      {/* Lápis só com personalização: reabre o popup já preenchido em vez de
          obrigar a remover e adicionar de novo. */}
      {itemEditavel(item) && (
        <button onClick={onEditar} className={`ml-auto ${botao} hover:bg-orange-500/20`} title="Editar item" aria-label="Editar este item">
          <Edit2 className={`${tam.icone} text-orange-500`} />
        </button>
      )}
    </div>
  );
}

function EtapasDoItem({ etapas, t, fotoDe, tam }) {
  if (etapas.length === 0) return null;
  return (
    <div className={`mt-2 pt-2 border-t ${t.border} space-y-2`}>
      {etapas.map((step, idx) => (
        <div key={idx}>
          <div className={`text-[9px] font-semibold ${t.textMuted} uppercase tracking-wide mb-1`}>{step.stepName}</div>
          <div className="space-y-1">
            {step.items.map((si, i) => (
              <div key={i} className="flex items-center gap-2">
                {si.photo_url && (
                  <div className={`${tam.etapaFoto} rounded-md ${t.bgMuted} flex-shrink-0 overflow-hidden`}>
                    <img src={fotoDe(si.photo_url)} alt="" className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`${tam.etapaNome} ${t.text} truncate leading-tight`}>
                    {si.qty > 1 && <span className={`${t.textMuted} mr-0.5`}>{si.qty}×</span>}
                    {si.name}
                  </p>
                  {si.price != null && si.price > 0 && (
                    <p className={`${tam.etapaPreco} text-orange-500 font-medium leading-tight`}>{reais(si.price)}</p>
                  )}
                  {si.gratis && (
                    <p className={`${tam.etapaPreco} text-green-500 font-medium leading-tight`}>Grátis</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
